import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';

import {
  ActiveEmployee,
  Backup,
  Employee,
  EmpRequestParams,
  Link,
  SelectEmployee,
  SendEmployee
} from './emp-tree.interfaces';

import {ApiService} from '../../shared/api/api.service';
import {QueryParamBuilder} from '../../shared/utils/query-param-builder';
import {getInitials, isValidPhotoUrl} from '../../shared/utils/strings';

@Injectable()
export class EmpTreeService {
  constructor(private api: ApiService) {

  }

  public getTreeRoots(): Observable<any> {
    return this.api.get('/repository/api/v1/global/entityTree?type=PEOPLE');
  }

  public getRepEntitiesShortViewByTree(url: string): any {
    return this.api.get(url);
  }

  public getTreeNodes(params: EmpRequestParams): Observable<any> {
    return this.api.get(`/ga/api/v1/peopleTree/getChain/`, {
      requestOptions: {
        params
      }
    })
      .pipe(
        tap((res: Employee[]) => {
          const ids: Set<number> = new Set<number>();

          res.forEach((employee: Employee) => ids.add(employee.id));
          res.forEach((employee: Employee) => {
            if (!ids.has(employee.parent)) {
              employee.parent = undefined;
            }
          });
        })
      );
  }



  public getNodeChildren(nodeId: number, effectiveDate: string) {
    const search: string = `?effectiveDate=${effectiveDate}&nodeId=${nodeId}`;

    return this.api.get(`/ga/api/v1/peopleTree/getChildren/${search}`);
  }

  public getPeopleManageNodes(url: string): Observable<any> {
    return this.api.get(url);
  }

  public getTreeOrgsByName(name: string, date: string, treeType?: string): Observable<any> {
    let url: string = `/ga/api/v1/peopleTree/find?effectiveDate=${date}&orderBy=cliEmp.firstName&search=${
      encodeURIComponent(name)
    }`;
    if (treeType) {
      url += `&treeType=${encodeURIComponent(treeType)}`;
    }

    return this.api.get(url);
  }

  public getTreeOrgsById(id: number) {
    const url: string = `/ga/api/v1/peopleTree/?search=cliEmp.id==${id}&orderBy=cliEmp.firstName`;

    return this.api.get(url);
  }

  public getEmployeesV2AsLegacyV1(search?: string, contractBeginDate?: string, contractEndDate?: string,
                                  perPage?: number): Observable<any> {
    return this.getEmployeesV2(search, contractBeginDate, contractEndDate, perPage)
      .pipe(
        map(employees => employees.map(employee => {
          employee.name = `${employee.firstName} ${employee.lastName}`;
          employee.photoLink = {
            rel: 'photo',
            href: `/ga/api/v1/cliEmp/${employee.id}/picture/${employee.photoUuid}`,
            type: 'GET'
          };
          employee.empContracts = employee.contracts;
          return employee;
        }))
      );
  }

  public getEmployeesV2(search?: string, contractBeginDate?: string, contractEndDate?: string, perPage?: number): Observable<any> {
    return this.api.get(
      new QueryParamBuilder('/ga/api/v2/employee')
        .add('order-by', 'lastName')
        .add('per-page', perPage, !!perPage)
        .addIfNotNull('search', search)
        .addIfNotNull('contract-begin-date', contractBeginDate)
        .addIfNotNull('contract-end-date', contractEndDate)
        .build());
  }

  public getEmployees(periodBegin?: string, periodEnd?: string): Observable<any> {
    return this.api.get(
      new QueryParamBuilder('/ga/api/v1/cliEmp/activeInPeriod')
        .add('orderBy', 'lastName')
        .addIfNotNull('periodBegin', periodBegin)
        .addIfNotNull('periodEnd', periodEnd)
        .build())
      .pipe(
        map((array) => {
          array.forEach((element) => {
            element.name = `${element.firstName} ${element.lastName}`;
          });
          return array;
        })
      );
  }

  public updateBackups(peopleTreeNodeId: number, backups: Backup[]): Observable<any> {
    return this.api.post(`/ga/api/v1/peopleTree/${peopleTreeNodeId}/backup`, backups);
  }

  public updateEmployee(peopleTreeNodeId: number, employee: SendEmployee): Observable<any> {
    return this.api.post(`/ga/api/v1/peopleTree/${peopleTreeNodeId}`, employee);
  }

  public replaceEmployee(replacedPeopleTreeNodeId: number, replacementCliEmpId?: number, replacementDate?: string) {
    return this.api.post(
      new QueryParamBuilder(`/ga/api/v1/peopleTree/${replacedPeopleTreeNodeId}/replace`)
        .add('replacementCliEmpId', replacementCliEmpId, !!replacementCliEmpId)
        .add('replacementDate', replacementDate)
        .build());
  }

  public getSelect(name: string, employeeId: string, avatarUrl?: Link): string {
    const hasPhoto = isValidPhotoUrl(avatarUrl?.href);
    const parts = name.trim().split(/\s+/);
    const initials = getInitials(parts[0], parts.length > 1 ? parts[parts.length - 1] : '');
    const avatar = hasPhoto
      ? `<span class="circle avatar avatar-text w-28">
            <img src="${this.api.apiHost + avatarUrl.href}" alt="">
        </span>`
      : `<span class="gp-initials-avatar gp-avatar-sm">${initials}</span>`;

    return `<div class="emp">
              <div class="emp-avatar">
                    ${avatar}
              </div>
              <div class="emp-info">
                <div>${name}</div>
                <small class="text-muted">${employeeId}</small>
            </div>
          </div>`;
  }

  public getEmployeeFromData(data: any): SendEmployee {
    return {
      backupBeginDate: data.backupBeginDate,
      backupEndDate: data.backupEndDate
    };
  }

  public generateSelectEmployeeList(employeeList: ActiveEmployee[], currentEmployeeId: number): SelectEmployee[] {
    return employeeList
      .filter((employee: ActiveEmployee) => employee.id !== currentEmployeeId)
      .map((element: ActiveEmployee) => {

        return <SelectEmployee>{
          id: element.id,
          select: this.getSelect(element.name, element.employeeNumber, element.photoLink),
          firstName: element.firstName,
          lastName: element.lastName
        };
      });
  }
}
